import {
  Checkbox,
  Col,
  Divider,
  Form,
  FormInstance,
  Input,
  Radio,
  Row,
  Typography,
} from 'antd';
import { useIntl } from 'react-intl';
import ReportSelectMonthFields from '@/components/_common/ReportSelectMonthFields';
import { CheckboxGroupButtons, FloatingFormItem } from '@/components/_common';
import {
  FilterByDateReportPurchase,
  GroupReportByPurchases,
  ItemsReportPurchase,
  PurchasesReportFormValues,
  SituationReportPurchase,
} from '@/core/domain/reports/purchases/purchases/PurchasesReport.types';
import PurchasesMoreOptions from './PurchasesMoreOptions';

interface Props {
  form: FormInstance<PurchasesReportFormValues>;
}

const PurchaseReportForm: React.FC<Props> = ({ form }) => {
  const intl = useIntl();
  const { Title } = Typography;
  const watchGroup = Form.useWatch('reportType', form);

  const situationOptions = [
    {
      label: intl.formatMessage({
        id: 'purchasesPurchaseReport.form.situation.open',
      }),
      value: SituationReportPurchase.open,
    },
    {
      label: intl.formatMessage({
        id: 'purchasesPurchaseReport.form.situation.authorized',
      }),
      value: SituationReportPurchase.authorized,
    },
    {
      label: intl.formatMessage({
        id: 'purchasesPurchaseReport.form.situation.canceled',
      }),
      value: SituationReportPurchase.canceled,
    },
  ];

  const itemsPurchaseOptions = [
    {
      label: intl.formatMessage({
        id: 'purchasesPurchaseReport.form.itemsPurchase.parts',
      }),
      value: ItemsReportPurchase.parts,
    },
    {
      label: intl.formatMessage({
        id: 'purchasesPurchaseReport.form.itemsPurchase.consumables',
      }),
      value: ItemsReportPurchase.consumables,
    },
    {
      label: intl.formatMessage({
        id: 'purchasesPurchaseReport.form.itemsPurchase.tasks',
      }),
      value: ItemsReportPurchase.tasks,
    },
    {
      label: intl.formatMessage({
        id: 'purchasesPurchaseReport.form.itemsPurchase.freightInvoice',
      }),
      value: ItemsReportPurchase.freightInvoice,
    },
  ];

  return (
    <Form layout="vertical" form={form}>
      <ReportSelectMonthFields
        form={form}
        dateFilter={
          <>
            <Title level={5}>
              {intl.formatMessage({
                id: 'purchasesPurchaseReport.form.filterByDate',
              })}
            </Title>

            <Form.Item
              name="dateType"
              noStyle
              initialValue={FilterByDateReportPurchase.launch}
            >
              <Radio.Group optionType="button" buttonStyle="solid" size="large">
                <Radio value={FilterByDateReportPurchase.launch}>
                  {intl.formatMessage({
                    id: 'purchasesPurchaseReport.form.filterByDate.launch',
                  })}
                </Radio>
                <Radio value={FilterByDateReportPurchase.emission}>
                  {intl.formatMessage({
                    id: 'purchasesPurchaseReport.form.filterByDate.emission',
                  })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </>
        }
      />

      <Row gutter={[16, 8]}>
        <Col sm={12}>
          <Title level={5}>
            {intl.formatMessage({
              id: 'purchasesPurchaseReport.form.situation',
            })}
          </Title>

          <Form.Item
            name="lsSituacao"
            initialValue={[SituationReportPurchase.open]}
            noStyle
          >
            <CheckboxGroupButtons options={situationOptions} />
          </Form.Item>
        </Col>

        <Col sm={12}>
          <Title level={5}>
            {intl.formatMessage({
              id: 'purchasesPurchaseReport.form.itemsPurchase',
            })}
          </Title>

          <Form.Item
            name="lsPurchaseItems"
            initialValue={[
              ItemsReportPurchase.parts,
              ItemsReportPurchase.consumables,
              ItemsReportPurchase.tasks,
              ItemsReportPurchase.freightInvoice,
            ]}
            noStyle
          >
            <CheckboxGroupButtons options={itemsPurchaseOptions} />
          </Form.Item>
        </Col>

        <Col sm={24}>
          <Title level={5}>
            {intl.formatMessage({
              id: 'purchasesPurchaseReport.form.groupReportBy',
            })}
          </Title>

          <Form.Item
            name="reportType"
            initialValue={GroupReportByPurchases.date}
          >
            <Radio.Group optionType="button" buttonStyle="solid" size="large">
              <Radio value={GroupReportByPurchases.date}>
                {intl.formatMessage({
                  id: 'purchasesPurchaseReport.form.groupReportBy.date',
                })}
              </Radio>
              <Radio value={GroupReportByPurchases.supplier}>
                {intl.formatMessage({
                  id: 'purchasesPurchaseReport.form.groupReportBy.supplier',
                })}
              </Radio>
              <Radio value={GroupReportByPurchases.workOrders}>
                {intl.formatMessage({
                  id: 'purchasesPurchaseReport.form.groupReportBy.workOders',
                })}
              </Radio>
              <Radio value={GroupReportByPurchases.cfop}>
                {intl.formatMessage({
                  id: 'purchasesPurchaseReport.form.groupReportBy.cfop',
                })}
              </Radio>
              <Radio value={GroupReportByPurchases.documentType}>
                {intl.formatMessage({
                  id: 'purchasesPurchaseReport.form.groupReportBy.documentType',
                })}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
        {watchGroup === GroupReportByPurchases.supplier && (
          <Col span={24}>
            <Form.Item name="synthetic" valuePropName="checked">
              <Checkbox>Sintético</Checkbox>
            </Form.Item>
          </Col>
        )}

        <Col span={24} md={8}>
          <FloatingFormItem
            name="purchaseCode"
            label={intl.formatMessage({
              id: 'purchasesPurchaseReport.form.purchaseCode',
            })}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            name="documentNumber"
            label={intl.formatMessage({
              id: 'purchasesPurchaseReport.form.documentNumber',
            })}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            name="invoiceKey"
            label={intl.formatMessage({
              id: 'purchasesPurchaseReport.form.accessKey',
            })}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Divider />

        <PurchasesMoreOptions form={form} />
      </Row>
      <Form.Item hidden noStyle name="suppliersIds" />
      <Form.Item hidden noStyle name="documentTypeIds" />
      <Form.Item hidden noStyle name="cfopIds" />
      <Form.Item hidden noStyle name="workOrderComplementIds" />
    </Form>
  );
};

export default PurchaseReportForm;
